import i18n from "i18next";

import {
  AgriculturePolicyDetails,
  HazardIdEnum,
  LocationRiskAssessment,
  PageTabs,
  PolicyDetails,
} from "../hazardIdEnum";
import { getNatcatHazardsList } from "./natcatHazardsList";
import { getFloodHazardsList } from "./floodHazardsList";
import { getHailHazardsList } from "./hailHazardsList";
import { getWindHazardsList } from "./windHazardsList";
import { getANIAHazardsList } from "./aniaHazardsList";
import { getWarningHazardsList } from "./warningHazardList";
import { getAllCocoHazardsList } from "./cocoHazardsList";
import { getProtezioneCivileHazardsList } from "./protezioneCivileHazardList";
import { getIcelandHazardsList } from "./icelandHazardsList";
import { socioEnvHazardsList } from "./socioEnvHazardsList";
import { nasaNowcastHazardsList } from "./nasaNowcastHazardList";
import { PermissionIdEnum } from "../../util/types/permissions/permissionIdEnum";

const getHazardsList = (): Hazard[] => [
  ...getNatcatHazardsList(),
  {
    cysId: HazardIdEnum.SeismicClassificationIta,
    name: "Seismic Classification (ITA)",
    // permissions: ["CYS_RISK_EARTHQUAKE_ITA_PROTCIV"],
    permissions: [PermissionIdEnum.SeismicClassificationIta],
    colorRange: ["#ececec", "#0984e3", "#00b894", "#fdcb6e", "#d63031"],
    description: i18n.t("riskExposureMapExplained.seismicClassification"),
    label: `${i18n.t("riskAssessment.seismicClassification")} (ITA)`,
    pages: [PolicyDetails, LocationRiskAssessment],
  },
  ...getFloodHazardsList(),
  ...getHailHazardsList(),
  {
    cysId: HazardIdEnum.ExplosionIta,
    name: "Explosion ITA",
    // permissions: ["CYS_RISK_EXPLOSION_ITA_MDTE"],
    permissions: [PermissionIdEnum.ExplosionIta],
    colorRange: ["#f9f9f9", "#fff5f0", "#fca486", "#ea372a", "#67000d"],
    description: i18n.t("riskExposureMapExplained.explosion"),
    label: `${i18n.t("riskAssessment.explosion")} (ITA)`,
    pages: [PolicyDetails, LocationRiskAssessment],
  },
  ...getWindHazardsList(),
  {
    cysId: HazardIdEnum.SnowIta5cm,
    name: "Snow ITA 5cm",
    // permissions: ["CYS_RISK_SNOW_ITA_EURAIN_5CM_RCP45_P2021-2050"],
    permissions: [PermissionIdEnum.SnowIta5cm],
    colorRange: ["#f9f9f9", "#8fcda7", "#FFFFBF", "#f6924e", "#d91d15"],
    description: i18n.t("riskExposureMapExplained.snowIta", { cm: 5 }),
    label: `${i18n.t("riskAssessment.snow")} (ITA) - 5cm`,
    pages: [PolicyDetails, AgriculturePolicyDetails, LocationRiskAssessment],
  },
  {
    cysId: HazardIdEnum.SnowIta25cm,
    name: "Snow ITA 25cm",
    // permissions: ["CYS_RISK_SNOW_ITA_EURAIN_25CM_RCP45_P2021-2050"],
    permissions: [PermissionIdEnum.SnowIta25cm],
    colorRange: ["#f9f9f9", "#8fcda7", "#FFFFBF", "#f6924e", "#d91d15"],
    description: i18n.t("riskExposureMapExplained.snowIta", { cm: 25 }),
    label: `${i18n.t("riskAssessment.snow")} (ITA) - 25cm`,
    pages: [PolicyDetails, AgriculturePolicyDetails, LocationRiskAssessment],
  },
  {
    cysId: HazardIdEnum.LightningStrikeIta,
    name: "Lightning Strike ITA",
    // permissions: ["CYS_RISK_LIGHTNING_ITA_NLDN_C"],
    permissions: [PermissionIdEnum.LightningStrikeIta],
    colorRange: ["#f9f9f9", "#2b83ba", "#abdda4", "#ffffbf", "#fdae61", "#d7191c"],
    description: i18n.t("riskExposureMapExplained.lightningStrike"),
    label: `${i18n.t("legend.lightningStrikeDensity")} (ITA)`,
    pages: [PolicyDetails, AgriculturePolicyDetails, LocationRiskAssessment],
  },
  ...getANIAHazardsList(),
  ...getWarningHazardsList(),
  {
    cysId: HazardIdEnum.RealeGroupHail,
    name: "Reale Group Hail",
    // permissions: ["CYS_RISK_RMA_HAIL"],
    permissions: [PermissionIdEnum.RealeGroupHail],
    colorRange: ["#f9f9f9", "#2A81BC", "#8FCCA7", "#DCF2B2", "#FEE196", "#F6924E", "#D91C15"],
    description: i18n.t("riskExposureMapExplained.realeGroupHail"),
    label: `Reale Group ${i18n.t("riskAssessment.hail")}`,
    pages: [PolicyDetails, LocationRiskAssessment],
    pageTab: PageTabs.preferredTab,
  },
  {
    cysId: HazardIdEnum.RealeGroupWind,
    name: "Reale Group Wind",
    // permissions: ["CYS_RISK_RMA_WIND"],
    permissions: [PermissionIdEnum.RealeGroupWind],
    colorRange: ["#f9f9f9", "#2A81BC", "#A9DEA2", "#FEE196", "#FEB05C", "#D91C15"],
    description: i18n.t("riskExposureMapExplained.realeGroupWind"),
    label: `Reale Group ${i18n.t("riskAssessment.highWind")}`,
    pages: [PolicyDetails, LocationRiskAssessment],
    pageTab: PageTabs.preferredTab,
  },

  ...getAllCocoHazardsList(),
  ...getProtezioneCivileHazardsList(),
  ...getIcelandHazardsList(),
  {
    cysId: HazardIdEnum.StormSurgeCat1Us,
    name: "Storm Surge Cat 1 (US)",
    // permissions: ["CYS_RISK_STORMSURGE_US_NOAA_CAT1"],
    permissions: [PermissionIdEnum.StormSurgeCat1Us],
    colorRange: ["#f9f9f9", "#1F78B4", "#F0F030", "#FA741B", "#FF0A12", "#6C6C6C"],
    description: "Storm Surge Cat 1 (US)",
    label: "Storm Surge Cat 1 (US)",
    pages: [LocationRiskAssessment],
  },
  {
    cysId: HazardIdEnum.WildfireVHPIndexUs,
    name: "Wildfire VHP Index (US)",
    // permissions: ["CYS_RISK_WILDFIRE_US_USDAFS_WP"],
    permissions: [PermissionIdEnum.WildfireVHPIndexUs],
    // 1 - "#4D8FC2",
    colorRange: ["#f9f9f9", "#2ABB8C", "#F9E03F", "#FAA945", "#CF000F"],
    description:
      "A wildfire hazard map covering the contiguous US that shows the relative potential for a wildfire in a given location. The value provided indicates the wildfire hazard at the policy’s location and is based on several parameters, such as burn probability, flame length probability and vegetation type. The data comes from the U.S. Department of Agriculture.",
    label: "Wildfire - WHP Index (US)",
    pages: [LocationRiskAssessment],
  },

  ...socioEnvHazardsList(),
  ...nasaNowcastHazardsList(),
];

export default getHazardsList;

// EarthquakeMagnitude

// seismic - proxy
// inland flood - proxy
// coastal flood && inland flood - proxy
// flash flood - proxy (all have the same value)
// inland flood global - proxy
// explosion ???
// hail - proxy
// WINDSTORM - proxy
// snow - proxy
// High Wind - proxy
// Lightning Strike - proxy
// ania hazards - proxy
// ispra - proxy
// natcat - natcat proxy, except for landslide

// still necessary?
// hail eur - value set from policy map line 736
// fire risk: value is string, hardcoded as Low -- removed
// Burglary Risk: value is string, hardcoded as Low - removed
// fema flood disabled, value = 0, hardcoded - keep
// Storm Surge (US) - proxy
// hail US
// tornado - proxy
// wind power density hardcoded 110 - should work
// WindSpeed hardcoded 8.00
